/* eslint-disable no-underscore-dangle */
import { createSelector } from 'reselect'
import createCachedSelector from 're-reselect'
import {
  firstEnduserActorFromDenormalizedEventGroup,
  getUniqueRecipients,
} from 'ducks/drafts2/util'
import { selectPreferredMailboxId } from 'ducks/currentUser/selectors/preferences/selectPreferredMailboxId'

import { selectMailboxIdFromUrl } from 'ducks/mailboxes/selectors/selectMailboxIdFromUrl'
import { selectFirstMailboxId } from 'ducks/mailboxes/selectors/selectFirstMailboxId'
import { selectCurrentMailboxId } from 'ducks/mailboxes/selectors/selectCurrentMailboxId'
import {
  selectIsAddingNote,
  selectIsLoggingNewConversation,
} from 'selectors/page'

import { emptyObj } from 'util/objects'
import { emptyArr } from 'util/arrays'
import { createBasicSelector } from 'util/redux'
import { extractTwitterHandles, stripHTML } from 'util/strings'
import { selectCurrentConversationById } from 'ducks/tickets/selectors'
import { selectConversationEventGroupsByConversationId } from 'ducks/tickets/selectors/selectConversationEventGroupsByConversationId'
import { selectCurrentUserPreferReassignTicketOnReply } from 'ducks/currentUser/selectors/preferences/selectCurrentUserPreferReassignTicketOnReply'
import { CLOSED, OPENED } from 'ducks/tickets/constants'
import { queryParams } from 'util/params'
import { selectCurrentUserId } from 'ducks/currentUser/selectors/selectCurrentUserId'
import { selectCurrentUserPreferReassignTicketOnNote } from 'ducks/currentUser/selectors/preferences/selectCurrentUserPreferReassignTicketOnNote'
import { selectAccountPrefersReplyOpen } from 'selectors/app/selectAccountPreferences'
import { selectCurrentAgentsById } from 'selectors/agents/base'
import { selectCurrentTeamsById } from 'ducks/teams/selectors'
import { assignmentLabel } from 'util/assignment'
import { twitterHandleFromContact } from 'ducks/customers/utils'
import { getAccountTwitterUsername } from 'util/ticket'
import { isFacebook, isTwitter } from 'ducks/tickets/utils/type'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { isGid } from 'util/globalId'

const storeKey = 'drafts2'

export const selectById = state => state[storeKey].byId || emptyObj

export const selectRawDraftsByTicketId = state => {
  return state[storeKey]?.byTicketId || emptyObj
}

export const selectByMessageId = state =>
  state[storeKey].byMessageId || emptyObj

export const selectDraftIdByTicketId = createBasicSelector(
  selectRawDraftsByTicketId,
  (_state, ticketId) => ticketId,
  (_state, _ticketId, type) => type,
  (byTicketId, ticketId, type) => {
    const draftIdSet = byTicketId[ticketId]
    return draftIdSet ? draftIdSet[type] : null
  }
)

export const selectDraftById = createBasicSelector(
  selectById,
  (_state, draftId) => draftId,
  (draftById, draftId) => draftById[draftId] || null
)

export const selectDraftBodyByDraftId = (state, draftId) => {
  const found = selectDraftById(state, draftId)
  return found ? found.body : null
}

export const selectAttachmentsByDraftId = (state, draftId) => {
  const found = selectDraftById(state, draftId)
  return found ? found.attachmentsNormalized : emptyArr
}

export const selectMailboxIdForDraftByTicketId = createBasicSelector(
  selectMailboxIdFromUrl,
  selectCurrentMailboxId,
  selectPreferredMailboxId,
  selectFirstMailboxId,
  (mailboxIdFromUrl, currentMailboxId, preferredMailboxId, firstMailboxId) =>
    mailboxIdFromUrl || currentMailboxId || preferredMailboxId || firstMailboxId
)

export const selectNewNoteDraftByTicketId = createCachedSelector(
  (state, ticketId) =>
    selectCurrentConversationById(state, ticketId, null, true),
  selectCurrentUserPreferReassignTicketOnNote,
  selectCurrentUserId,
  selectMailboxIdForDraftByTicketId,
  (_state, ticketId) => ticketId,
  (
    ticket,
    reassignTicketOnNote,
    currentUserId,
    currentOrFirstMailboxId,
    ticketId
  ) => {
    const draftDefaults = {
      ticketId,
      isNote: true,
      assigneeId: ticket?.assigned?.agent?.id,
      assigneeGroupId: ticket?.assigned?.team?.id,
      mailboxId: currentOrFirstMailboxId,
    }
    if (reassignTicketOnNote) {
      draftDefaults.assigneeId = currentUserId
    }
    draftDefaults.state = ticket?.state || OPENED
    return draftDefaults
  }
)((_state, ticketId, type) => `${ticketId || 'unknown'}-${type || 'unknown'}`)

export const selectNewConversationDraftByTicketId = createSelector(
  selectIsLoggingNewConversation,
  selectMailboxIdForDraftByTicketId,
  selectCurrentUserId,
  selectCurrentUserPreferReassignTicketOnReply,
  (
    isLoggingNewConversation,
    currentOrFirstMailboxId,
    currentUserId,
    reassignTicketOnReply
  ) => {
    const isNote = isLoggingNewConversation
    const params = queryParams()
    const title = params.subject || ''
    const body = params.body || ''
    const to = []
    const cc = []
    const bcc = []

    return {
      replyType: 'reply-all',
      mailboxId: currentOrFirstMailboxId,
      assigneeId: reassignTicketOnReply ? currentUserId : null,
      assigneeGroupId: null,
      to,
      cc,
      bcc,
      isNote,
      body,
      title,
    }
  }
)

export const selectReplyDraftByTicketId = createCachedSelector(
  (state, ticketId) =>
    selectCurrentConversationById(state, ticketId, null, true),
  (state, ticketId) =>
    selectConversationEventGroupsByConversationId(state, ticketId, true),
  selectCurrentUserId,
  selectCurrentUserPreferReassignTicketOnReply,
  selectAccountPrefersReplyOpen,
  (_state, ticketId) => ticketId,
  (
    ticket,
    eventGroups,
    currentUserId,
    reassignTicketOnReply,
    prefersReplyOpen,
    ticketId
  ) => {
    // Either the ticket doesnt exist or its loading. Either way we cant setup a reply
    // draft with no ticket
    if (!ticket || !eventGroups || eventGroups.length === 0) return null

    const channel = ticket.channel
    const draftDefaults = {
      ticketId,
      replyType: 'reply-all',
      mailboxId: channel?.id,
      assigneeId: ticket.assigned?.agent?.id,
      assigneeGroupId: ticket.assigned?.team?.id,
      state: CLOSED,
    }
    if (reassignTicketOnReply) {
      draftDefaults.assigneeId = currentUserId
    }
    if (prefersReplyOpen) {
      draftDefaults.state = OPENED
    }

    const firstReply = eventGroups.find(eg => eg.isFirstMessage)
    const lastReply = [...eventGroups].reverse().find(eg => !eg.isNote)

    if (lastReply && firstReply !== lastReply) {
      // When the ticket has more than message

      // If we've merged 2 converations, we also want to go ahead and merge the cc and bcc
      // fromt the 2 conversations so that the new reply includes everyone
      if (lastReply.fromMerge) {
        draftDefaults.cc = getUniqueRecipients(
          draftDefaults.cc,
          lastReply.summary?.cc
        )
        draftDefaults.bcc = getUniqueRecipients(
          draftDefaults.bcc,
          lastReply.summary?.bcc
        )
      } else {
        draftDefaults.cc = lastReply.summary?.cc
        draftDefaults.bcc = lastReply.summary?.bcc
      }

      const firstEndUserActor = firstEnduserActorFromDenormalizedEventGroup(
        firstReply
      )
      const lastEndUserActor = firstEnduserActorFromDenormalizedEventGroup(
        lastReply
      )

      // In our system we have the ability to change the enduser linked to the ticket
      // If the first and the last message have gone to the same actor, always use the contact
      // from the ticket to make sure that if customers change the contact on the ticket, the newq
      // contact is used
      if (firstEndUserActor?.id === lastEndUserActor?.id) {
        draftDefaults.to = [ticket.contact]
      } else if (
        lastReply.actor?.__typename === 'Agent' &&
        lastReply.summary?.to &&
        lastReply.summary?.to.length > 0
      ) {
        draftDefaults.to = lastReply.summary?.to
      } else if (
        lastReply.actor != null &&
        lastReply.actor.__typename !== 'Agent'
      ) {
        draftDefaults.to = [lastReply.actor]
      } else if (channel) {
        // This fallback is implemetned due to the original fallback that was implemented
        // over here app/serializers/v1/comment_change_serializer.rb:64 in the to method
        draftDefaults.to = [{ name: channel.name, email: channel.email }]
      }
    } else if (lastReply) {
      // When the ticket only has 1 message
      draftDefaults.to = [ticket.contact]
      draftDefaults.cc = lastReply.summary?.cc
      draftDefaults.bcc = lastReply.summary?.bcc
    } else {
      // When the ticket has no messages
      draftDefaults.to = [ticket.contact]
    }
    return draftDefaults
  }
)((_state, ticketId, type) => `${ticketId || 'unknown'}-${type || 'unknown'}`)

export const selectTwitterReplyDraftByTicketId = createCachedSelector(
  selectReplyDraftByTicketId,
  (state, ticketId) =>
    selectCurrentConversationById(state, ticketId, null, true),
  (state, ticketId) =>
    selectConversationEventGroupsByConversationId(state, ticketId, true),
  (replyDraft, ticket, eventGroups) => {
    const twitterDraft = { ...replyDraft }
    const contact = ticket.contact
    twitterDraft.isTwitter = true

    const twitterRecipients = []
    const twitterHandles = []
    const to = twitterDraft.to && twitterDraft.to[0]

    if (twitterHandleFromContact(to)) {
      twitterRecipients.push(to)
      twitterHandles.push(`@${twitterHandleFromContact(to)}`)
    } else if (twitterHandleFromContact(contact)) {
      twitterRecipients.push({
        twitter: { username: twitterHandleFromContact(contact) },
      })
      twitterHandles.push(`@${twitterHandleFromContact(contact)}`)
    }
    delete twitterDraft.to
    delete twitterDraft.cc
    delete twitterDraft.bcc

    const firstReply = eventGroups.find(eg => eg.isFirstMessage)
    const lastReply = [...eventGroups].reverse().find(eg => !eg.isNote)
    const customerMessage = lastReply || firstReply

    if (customerMessage) {
      const {
        summary: { bodyPlainText },
      } = customerMessage

      const actorHandle = twitterHandleFromContact(customerMessage.actor)
      const accountHandle = getAccountTwitterUsername(ticket)
      const otherHandles = extractTwitterHandles(bodyPlainText).filter(
        item => item !== accountHandle
      )

      const handles = [actorHandle, ...otherHandles].filter(h => !!h)
      handles.forEach(handle => {
        if (handle) {
          const username = handle.substr(1)
          if (
            twitterRecipients.length === 0 ||
            (twitterRecipients.length > 0 &&
              twitterRecipients[0].customFieldValues?.contact_twitter?.value
                ?.text &&
              twitterRecipients[0].customFieldValues?.contact_twitter?.value
                ?.text !== username)
          ) {
            twitterHandles.push(`@${username}`)
            twitterRecipients.push({
              customFieldValues: {
                contact_twitter: { value: { text: username } },
              },
            })
          }
        }
      })
    }

    twitterDraft.twitterPrefix =
      twitterHandles.length > 0 && `${twitterHandles.join(' ')} `
    twitterDraft.twitterRecipients = twitterRecipients

    return twitterDraft
  }
)((_state, ticketId, type) => `${ticketId || 'unknown'}-${type || 'unknown'}`)

export const selectFacebookReplyDraftByTicketId = createCachedSelector(
  selectReplyDraftByTicketId,
  replyDraft => {
    const facebookDraft = { ...replyDraft }
    facebookDraft.isFacebook = true

    const to = facebookDraft.to && facebookDraft.to[0]

    delete facebookDraft.cc
    delete facebookDraft.bcc
    facebookDraft.facebookRecipient = to

    return facebookDraft
  }
)((_state, ticketId, type) => `${ticketId || 'unknown'}-${type || 'unknown'}`)

export const selectDraftDefaultByTicketId = createBasicSelector(
  (state, ticketId, draftType) => {
    const ticket = selectCurrentConversationById(state, ticketId)

    if (draftType === 'note') {
      return selectNewNoteDraftByTicketId(state, ticketId, draftType)
    } else if (ticketId === 'new') {
      return selectNewConversationDraftByTicketId(state, ticketId, draftType)
    } else if (isTwitter(ticket)) {
      return selectTwitterReplyDraftByTicketId(state, ticketId, draftType)
    } else if (isFacebook(ticket)) {
      return selectFacebookReplyDraftByTicketId(state, ticketId, draftType)
    }
    return selectReplyDraftByTicketId(state, ticketId, draftType)
  }
)

export const selectDraftByTicketId = createBasicSelector(
  selectById,
  selectDraftIdByTicketId,
  selectDraftDefaultByTicketId,
  (byId, draftId, draftDefaults) => {
    const draft = byId[draftId]
    return draft || draftDefaults
  }
)

export const selectDraftBodyByTicketId = (state, ticketId, type) => {
  const found = selectDraftByTicketId(state, ticketId, type)
  return found ? found.body : null
}

export const selectCurrentTicketReplyDraft = createBasicSelector(
  selectCurrentTicketId,
  state => state,
  (ticketId, state) => selectDraftByTicketId(state, ticketId, 'reply')
)

export const selectCurrentTicketNoteDraft = createBasicSelector(
  selectCurrentTicketId,
  state => state,
  (ticketId, state) => selectDraftByTicketId(state, ticketId, 'note')
)

export const selectCurrentTicketDraftByInferredType = createSelector(
  selectIsAddingNote,
  selectCurrentTicketNoteDraft,
  selectCurrentTicketReplyDraft,
  (isAddingNote, noteDraft, replyDraft) =>
    isAddingNote ? noteDraft : replyDraft
)

export const selectCurrentRecipientSyncSource = state =>
  state[storeKey].currentRecipientSync?.source

export const selectIsSyncingRecipients = createBasicSelector(
  selectCurrentRecipientSyncSource,
  source => !!source
)

export const selectCurrentTicketReplyDraftHasBody = createSelector(
  selectCurrentTicketReplyDraft,
  draft => !!(draft && draft.body && stripHTML(draft.body))
)

export const selectCurrentTicketReplyDraftTo = createSelector(
  selectCurrentTicketReplyDraft,
  draft => draft?.to
)

export const selectCurrentTicketReplyDraftAllContactIds = createSelector(
  selectCurrentTicketReplyDraft,
  draft => {
    if (!draft) return []
    const all = {}
    ;['to', 'cc', 'bcc'].forEach(type => {
      const recipients = draft[type] || []
      recipients.forEach(r => {
        if (r.contact_id) {
          all[r.contact_id] = r.contact_id
        } else if (isGid(r.id)) {
          all[r.id] = r.id
        }
      })
    })

    return Object.values(all)
  }
)

export const selectDraftAssigneeIdByTicketId = createBasicSelector(
  selectDraftByTicketId,
  draft => draft?.assigneeId
)

export const selectDraftAssigneeByTicketId = createBasicSelector(
  selectDraftAssigneeIdByTicketId,
  selectCurrentAgentsById,
  (assigneeId, agentsById) => agentsById[assigneeId]
)

export const selectDraftAssigneeGroupIdByTicketId = createBasicSelector(
  selectDraftByTicketId,
  draft => draft?.assigneeGroupId
)

export const selectDraftAssigneeGroupByTicketId = createBasicSelector(
  selectDraftAssigneeGroupIdByTicketId,
  selectCurrentTeamsById,
  (assigneeGroupId, teamsById) => teamsById[assigneeGroupId]
)

export const selectDraftAssignmentLabelByTicketId = createCachedSelector(
  selectDraftAssigneeByTicketId,
  selectDraftAssigneeGroupByTicketId,
  selectCurrentUserId,
  assignmentLabel
)((_state, ticketId, type) => `${ticketId || 'unknown'}-${type || 'unknown'}`)

export const selectCurrentDraftContactId = createBasicSelector(
  selectCurrentTicketDraftByInferredType,
  draft => {
    return draft?.to?.[0]?.contact_id
  }
)

export const selectCurrentDraftContactIdOrTemp = createSelector(
  selectCurrentTicketId,
  selectCurrentDraftContactId,
  (ticketId, contactId) => {
    return contactId || (ticketId && `forConversation:${ticketId}`)
  }
)
